import { Search } from 'lucide-react';
import type { Ref } from 'react';

import { Input } from '@/_shared/components/ui/input';
import { cn } from '@/_shared/lib/utils';

interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  /** Accessible name for the field. Defaults to `placeholder`, which is the visible label. */
  ariaLabel?: string;
  /**
   * The placeholder, and it is STATIC.
   *
   * This used to accept a `placeholders` array that cycled with a typewriter effect. It is gone on
   * purpose and should not come back: motion in a text field draws the eye to the one element on a
   * list page that is supposed to wait quietly until it is wanted, and it animated forever rather
   * than settling. A field that keeps rewriting itself in the reader's periphery is a cost paid on
   * every render of every list, to advertise search syntax that the field's own results teach better.
   *
   * If several phrasings are genuinely worth telling a reader, the honest place is one static string
   * naming the primary field plus a `title`/help affordance — not a loop they cannot pause or read at
   * their own speed.
   */
  placeholder?: string;
  className?: string;
  ref?: Ref<HTMLInputElement>;
  shortcutHint?: string;
  size?: 'md' | 'lg';
}

export function SearchInput({
  value,
  onChange,
  ariaLabel,
  placeholder = 'Search...',
  className,
  ref,
  shortcutHint,
  size = 'md',
}: SearchInputProps): React.ReactElement {
  return (
    <div className={cn('relative', className)}>
      <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        ref={ref}
        type="search"
        aria-label={ariaLabel ?? placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            e.currentTarget.blur();
          }
        }}
        size={size}
        placeholder={placeholder}
        className={cn('peer pl-8', shortcutHint && !value && 'pr-8.5')}
      />
      {shortcutHint && !value && (
        <kbd
          aria-hidden="true"
          className="pointer-events-none absolute top-1/2 right-1.5 -translate-y-1/2 rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground peer-focus:hidden"
        >
          {shortcutHint}
        </kbd>
      )}
    </div>
  );
}
